// ============================================
// STATUS BADGE COMPONENT
// ============================================

import { cn } from '@/utils';

export type StatusVariant = 
  | 'default' 
  | 'success' 
  | 'warning' 
  | 'error' 
  | 'info'
  | 'ACTIVE'
  | 'DRAFT'
  | 'PAUSED'
  | 'CLOSED'
  | 'EXPIRED'
  | 'PENDING'
  | 'REVIEWED'
  | 'SHORTLISTED'
  | 'INTERVIEW'
  | 'OFFERED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'WITHDRAWN'
  | 'APPROVED';

interface StatusBadgeProps {
  status: string;
  label?: string;
  dot?: boolean;
  className?: string;
}

const statusConfig: Record<string, { color: string; bg: string; label: string }> = {
  // Job statuses
  ACTIVE: { color: 'text-green-700', bg: 'bg-green-100', label: 'Aktif' },
  DRAFT: { color: 'text-gray-700', bg: 'bg-gray-100', label: 'Taslak' },
  PAUSED: { color: 'text-amber-700', bg: 'bg-amber-100', label: 'Duraklatıldı' },
  CLOSED: { color: 'text-red-700', bg: 'bg-red-100', label: 'Kapatıldı' },
  EXPIRED: { color: 'text-gray-700', bg: 'bg-gray-100', label: 'Süresi Doldu' },
  
  // Application statuses
  PENDING: { color: 'text-amber-700', bg: 'bg-amber-100', label: 'Beklemede' },
  REVIEWED: { color: 'text-blue-700', bg: 'bg-blue-100', label: 'İncelendi' },
  SHORTLISTED: { color: 'text-purple-700', bg: 'bg-purple-100', label: 'Ön Elemede' },
  INTERVIEW: { color: 'text-indigo-700', bg: 'bg-indigo-100', label: 'Mülakat' },
  OFFERED: { color: 'text-teal-700', bg: 'bg-teal-100', label: 'Teklif Yapıldı' },
  ACCEPTED: { color: 'text-green-700', bg: 'bg-green-100', label: 'Kabul Edildi' },
  REJECTED: { color: 'text-red-700', bg: 'bg-red-100', label: 'Reddedildi' },
  WITHDRAWN: { color: 'text-gray-700', bg: 'bg-gray-100', label: 'Geri Çekildi' },
  APPROVED: { color: 'text-green-700', bg: 'bg-green-100', label: 'Onaylandı' },
  
  // Generic statuses
  success: { color: 'text-green-700', bg: 'bg-green-100', label: 'Başarılı' },
  warning: { color: 'text-amber-700', bg: 'bg-amber-100', label: 'Uyarı' },
  error: { color: 'text-red-700', bg: 'bg-red-100', label: 'Hata' },
  info: { color: 'text-blue-700', bg: 'bg-blue-100', label: 'Bilgi' },
  default: { color: 'text-gray-700', bg: 'bg-gray-100', label: '-' },
};

export function StatusBadge({ status, label, dot = false, className }: StatusBadgeProps) {
  const config = statusConfig[status] || statusConfig.default;
  const displayLabel = label || config.label;
  
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full',
        config.bg,
        config.color,
        className
      )}
    >
      {dot && (
        <span className={cn('w-1.5 h-1.5 rounded-full', config.color.replace('text-', 'bg-'))} />
      )}
      {displayLabel}
    </span>
  );
}

// ============================================
// VERIFICATION BADGE COMPONENT
// ============================================

interface VerificationBadgeProps {
  verified: boolean;
  className?: string;
}

export function VerificationBadge({ verified, className }: VerificationBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full',
        verified
          ? 'bg-green-100 text-green-700'
          : 'bg-gray-100 text-gray-600',
        className
      )}
    >
      {verified ? (
        <>
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          Doğrulanmış
        </>
      ) : (
        'Doğrulanmamış'
      )}
    </span>
  );
}
